import DOMPurify from "dompurify";
import pathOf from "./pathOf";
import renameStyleAttributes from "./renameStyleAttributes";

// Long enough for a block page's explanation and contact, short enough to keep the event small.
const textLimit = 300;
const emailDomainLimit = 5;

const emailDomain = /@([a-z0-9-]+(?:\.[a-z0-9-]+)+)/gi;

const collapse = (text) => (text || "").replace(/\s+/g, " ").trim().slice(0, textLimit);

// The media type alone, and only when it has the shape of one.
const mediaTypeOf = (contentType) => {
  const mediaType = (contentType || "").split(";")[0].trim().toLowerCase();

  return /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(mediaType) ? mediaType : "";
};

// The product name a Server header starts with ("Zscaler/6.2" → "Zscaler"), and nothing after it.
const vendorOf = (server) => {
  const match = (server || "").match(/^[A-Za-z0-9._-]{1,40}/);

  return match ? match[0] : "";
};

// The domains of the email addresses a page shows, which name the IT team behind it.
const emailDomainsIn = (text) => {
  const domains = Array.from(text.matchAll(emailDomain), ([, domain]) => domain.toLowerCase());

  return [...new Set(domains)].slice(0, emailDomainLimit);
};

// Joined with spaces, since textContent runs the words of neighbouring blocks together.
const textOf = (page) => {
  const walker = document.createTreeWalker(page, NodeFilter.SHOW_TEXT);
  const parts = [];

  while (walker.nextNode()) {
    parts.push(walker.currentNode.nodeValue);
  }

  return parts.join(" ");
};

// This page's Content-Security-Policy checks a <style> element and a style attribute as they are
// parsed, even into a document that is never shown, so both are renamed first.
const renamedStyle = "data-style";
const renameStyles = (html) => renameStyleAttributes(html, renamedStyle)
  .replace(/<(\/?)style\b/gi, `<$1${renamedStyle}`);

// Only text is read from the page. DOMPurify parses it into a document that is never shown, where
// its scripts never run and nothing it references loads, and keeps no text from its scripts,
// <noscript>, <template> or <style> elements.
const pageMarkup = { WHOLE_DOCUMENT: true, RETURN_DOM: true, ADD_TAGS: ["title"], ADD_FORBID_CONTENTS: [renamedStyle] };

const readPage = (body, contentType) => {
  if (!/html/i.test(contentType)) {
    return { title: "", text: collapse(body), emailDomains: emailDomainsIn(body) };
  }

  const page = DOMPurify.sanitize(renameStyles(body), pageMarkup);
  const titleElement = page.querySelector("title");
  const title = (titleElement || {}).textContent || "";
  if (titleElement) {
    titleElement.remove();
  }
  const text = textOf(page);

  return { title: collapse(title), text: collapse(text), emailDomains: emailDomainsIn(`${title} ${text}`) };
};

// The facts about a problem response. Its title and text are for the person who hit it, on this
// page; only the structured facts are fit to report.
export default function describeResponse({ method, url, status, requestId, header, body }) {
  const contentType = header("content-type") || "";

  return {
    status: String(status),
    method: method || "",
    requestPath: pathOf(url),
    contentType: mediaTypeOf(contentType),
    vendor: vendorOf(header("server")),
    requestId: requestId || "",
    ...readPage(body || "", contentType),
  };
}
